import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api, fmtDate, formatApiError } from "../lib/api";

export const Stars = ({ value, onChange, size = "h-9 w-9", testid = "review-star" }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} data-testid={`${testid}-${n}`} onClick={() => onChange?.(n)} className="transition-transform hover:scale-110 disabled:hover:scale-100">
        <Star className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

function GoogleCta({ url }) {
  if (!url) return null;
  return (
    <div className="mt-6 rounded-xl bg-blue-50 p-4 text-sm text-slate-700" data-testid="review-google-cta">
      <p>Merci pour cette belle note ! Vous pouvez aussi aider d'autres familles en publiant votre avis sur Google.</p>
      <a href={url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#1559d6] px-4 py-2 font-semibold text-white" data-testid="review-google-link"><ExternalLink className="h-4 w-4" />Publier sur Google</a>
    </div>
  );
}

export default function PublicReview() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/public/reviews/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(formatApiError(e)));
  }, [token]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/public/reviews/${token}`, { rating, comment });
      setDone(r.data);
    } catch (e) {
      setError(formatApiError(e));
    }
    setBusy(false);
  };

  const answered = done || info?.review;
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f6f8fb] px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8" data-testid="public-review">
        {!info && !error && <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#1559d6]" />}
        {error && !info && <p className="text-center text-sm text-red-600" data-testid="review-error">{error}</p>}
        {info && (
          <>
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600">{info.company_name}</p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">Comment s'est passé votre déménagement ?</h1>
            <p className="mt-1 text-sm text-slate-500">{info.client_name} · {fmtDate(info.move_date)}</p>
            {answered ? (
              <div className="mt-6" data-testid="review-thanks">
                <p className="flex items-center gap-2 font-semibold text-teal-700"><CheckCircle2 className="h-5 w-5" />Merci, votre avis a bien été enregistré.</p>
                <GoogleCta url={done ? done.google_review_url : info.google_review_url} />
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                <Stars value={rating} onChange={setRating} />
                <Textarea data-testid="review-comment-input" rows={4} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Racontez-nous en quelques mots (facultatif)" />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button data-testid="review-submit-button" disabled={!rating || busy} onClick={submit} className="w-full bg-[#0b2c6b] hover:bg-[#1559d6]">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer mon avis"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
